<template>
  <div class="mb-4 flex shrink-0 items-center justify-end">
    <span class="mr-2 text-xs text-background-fg/87">
      Report for month:
    </span>
    <ReportDateSelect
      v-model="filters.forMonthStartingOn"
      type="month"
      disable-current-month
    />
  </div>
  <template v-if="engagementReport">
    <BaseCard class="p-0">
      <HeadlessTabGroup>
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <HeadlessTabList
            class="flex flex-col bg-background-fg/3"
          >
            <HeadlessTab
              v-for="(
                info, key
              ) in engagementReportStructured"
              :key="key"
              #default="{ selected }"
              as="template"
            >
              <button
                type="button"
                class="flex w-full items-center justify-start border-b border-background-fg/8 p-2"
                :class="
                  selected ? 'bg-background' : 'border-r'
                "
              >
                <div
                  class="mr-4 flex size-8 shrink-0 items-center justify-center rounded-full font-semibold text-background"
                  :class="
                    getBackgroundColorClass(info.color)
                  "
                >
                  {{ info.score ?? "-" }}
                </div>
                <h3
                  v-if="info.label"
                  class="text-sm font-semibold text-background-fg/70"
                >
                  {{ info.label }}
                </h3>
              </button>
            </HeadlessTab>
          </HeadlessTabList>

          <HeadlessTabPanels>
            <HeadlessTabPanel
              v-for="(
                info, key
              ) in engagementReportStructured"
              :key="key"
            >
              <dl class="space-y-2 p-2">
                <div class="rounded border p-1">
                  <template
                    v-for="(
                      comparisonValue, comparisonKey
                    ) in info.comparisons"
                    :key="comparisonKey"
                  >
                    <div
                      v-if="
                        comparisonValue !== undefined &&
                        comparisonValue !== null &&
                        info.score !== undefined &&
                        info.score !== null
                      "
                      class="flex justify-between"
                    >
                      <div
                        class="text-sm text-background-fg/70"
                      >
                        <template
                          v-if="
                            comparisonKey === 'takepayments'
                          "
                        >
                          Takepayments+ average
                        </template>
                        <template
                          v-else-if="
                            comparisonKey === 'mcc'
                          "
                        >
                          MCC average ({{
                            engagementReport.merchantCategoryCode
                          }})
                        </template>
                        <template
                          v-else-if="
                            comparisonKey === 'sector'
                          "
                        >
                          Sector average ({{
                            engagementReport.merchantSector
                          }})
                        </template>
                      </div>

                      <div class="flex items-center">
                        <span class="text-sm font-medium">
                          {{
                            Number(
                              `${Math.round(
                                Number(
                                  `${comparisonValue}e2`
                                )
                              )}e-2`
                            )
                          }}
                        </span>

                        <div
                          class="ml-2 flex size-6 items-center justify-center"
                        >
                          <p
                            v-if="
                              comparisonValue === info.score
                            "
                            class="text-lg text-info"
                          >
                            =
                          </p>

                          <IconChevronDoubleDown
                            v-else-if="
                              comparisonValue > info.score
                            "
                            class="text-orange"
                          />
                          <IconChevronDoubleUp
                            v-else
                            class="text-green"
                          />
                        </div>
                      </div>
                    </div>
                  </template>
                </div>

                <template
                  v-for="detail in info.details"
                  :key="detail.label"
                >
                  <div
                    :class="[
                      detail.value === true
                        ? 'flex justify-between bg-positive/8'
                        : detail.value === false
                          ? 'bg-error/8'
                          : '',
                      'flex items-center justify-between rounded px-2 py-1',
                    ]"
                  >
                    <dt
                      class="text-xs text-background-fg/50"
                    >
                      {{ detail.label }}
                    </dt>
                    <dd class="ml-2 shrink-0">
                      <template
                        v-if="detail.value === true"
                      >
                        <div
                          class="flex size-4 items-center justify-center rounded"
                        >
                          <IconCheck
                            class="text-positive"
                          />
                        </div>
                      </template>
                      <template
                        v-else-if="detail.value === false"
                      >
                      </template>
                      <template
                        v-else-if="
                          detail.value !== undefined &&
                          detail.value !== null
                        "
                      >
                        <span class="text-sm font-medium">
                          {{ detail.value }}
                        </span>
                      </template>
                      <template v-else> - </template>
                    </dd>
                  </div>
                </template>
              </dl>
            </HeadlessTabPanel>
          </HeadlessTabPanels>
        </div>
      </HeadlessTabGroup>
    </BaseCard>
  </template>
  <template v-else-if="isEngagementReportFetching">
    <div class="size-32">
      <BaseInnerLoading spinner-class="size-8" />
    </div>
  </template>
  <template v-else>
    <p
      class="h-32 text-center text-sm text-background-fg/87"
    >
      Merchant does not have an engagement report for this
      month
    </p>
  </template>
</template>

<script setup lang="ts">
const props = defineProps<{
  tenantId: string
}>()
const { defaultMerchantCurrency } = useOrganizationConfig()

const filters = ref({
  forMonthStartingOn: DateFns.format(
    DateFns.setDate(DateFns.addMonths(new Date(), -1), 1),
    "yyyy-MM-dd"
  ),
})

const {
  data: engagementReport,
  isFetching: isEngagementReportFetching,
} = useEngagementReport(
  computed(() => parseInt(props.tenantId)),
  filters
)

const getRagColor = (score: number | undefined) => {
  switch (
    score !== undefined ? Math.floor(score) : undefined
  ) {
    case 0:
      return "red"
    case 1:
      return "red"
    case 2:
      return "orange"
    case 3:
      return "yellow"
    case 4:
      return "lime"
    case 5:
      return "green"
    default:
      return "background-fg"
  }
}

// TODO: branding
type EngagementComparisons = Record<
  "takepayments" | "mcc" | "sector",
  number | null | undefined
>

type AvailableColor =
  | "primary"
  | "red"
  | "orange"
  | "yellow"
  | "lime"
  | "green"
  | "background-fg"

type EngagementReportStructured = Record<
  | "engagement"
  | "portal"
  | "products"
  | "features"
  | "reports",
  {
    label: string
    score: number | undefined
    comparisons: EngagementComparisons
    details: {
      label: string
      value: unknown
    }[]
    color: AvailableColor
  }
>

const engagementReportStructured =
  computed<EngagementReportStructured>(() => ({
    engagement: {
      label: "Engagement",
      score: Number(
        engagementReport.value?.engagementScore.toFixed(1)
      ),
      comparisons: {
        takepayments:
          engagementReport.value?.engagementScoreTpAvg,
        mcc: engagementReport.value?.engagementScoreMccAvg,
        sector:
          engagementReport.value?.engagementScoreSectorAvg,
      },
      details: [
        {
          label: "Transaction total",
          value: formatToCurrencyString(
            engagementReport.value?.totalTransactions ?? 0,
            engagementReport.value?.merchantCurrency ??
              defaultMerchantCurrency.value
          ),
        },
        {
          label:
            "Average weekly transaction volume (over full weeks within last 2 months)",
          value: formatToCurrencyString(
            engagementReport.value
              ?.averageWeeklyTransactions ?? 0,
            engagementReport.value?.merchantCurrency ??
              defaultMerchantCurrency.value
          ),
        },
        {
          label: "Average transaction value",
          value: formatToCurrencyString(
            engagementReport.value
              ?.averageTransactionValue ?? 0,
            engagementReport.value?.merchantCurrency ??
              defaultMerchantCurrency.value
          ),
        },
      ],
      color: "primary",
    },
    products: {
      label: "Products",
      score: engagementReport.value?.productScore,
      comparisons: {
        takepayments:
          engagementReport.value?.productScoreTpAvg,
        mcc: engagementReport.value?.productScoreMccAvg,
        sector:
          engagementReport.value?.productScoreSectorAvg,
      },
      details: [
        {
          label: "Number of products",
          value: engagementReport.value?.countOfProducts,
        },
        {
          label: "Number of categories",
          value: engagementReport.value?.countOfCategories,
        },
        {
          label:
            "Percentage of orders containing a product",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingProducts,
        },
        {
          label:
            "Percentage of orders using a note on a custom item",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingNotes,
        },
      ],
      color: getRagColor(
        engagementReport.value?.productScore ?? 0
      ),
    },
    reports: {
      label: "Reports",
      score: engagementReport.value?.reportingScore,
      comparisons: {
        takepayments:
          engagementReport.value?.reportingScoreTpAvg,
        mcc: engagementReport.value?.reportingScoreMccAvg,
        sector:
          engagementReport.value?.reportingScoreSectorAvg,
      },
      details: [
        {
          label: `Has${
            engagementReport.value
              ?.hasViewedDeviceReports === false
              ? " not"
              : ""
          } viewed device reports`,
          value:
            engagementReport.value?.hasViewedDeviceReports,
        },
        {
          label: `Has${
            engagementReport.value
              ?.hasViewedMultipleReportTypes === false
              ? " not"
              : ""
          } viewed multiple report types`,
          value:
            engagementReport.value
              ?.hasViewedMultipleReportTypes,
        },
      ],
      color: getRagColor(
        engagementReport.value?.reportingScore
      ),
    },
    portal: {
      label: "Portal",
      score: engagementReport.value?.portalScore,
      comparisons: {
        takepayments:
          engagementReport.value?.portalScoreTpAvg,
        mcc: engagementReport.value?.portalScoreMccAvg,
        sector:
          engagementReport.value?.portalScoreSectorAvg,
      },
      details: [
        {
          label: `Has${
            engagementReport.value
              ?.hasViewedPortalSummary === false
              ? " not"
              : ""
          } viewed portal summary`,
          value:
            engagementReport.value?.hasViewedPortalSummary,
        },
        {
          label: `Has${
            engagementReport.value
              ?.hasViewedPortalReports === false
              ? " not"
              : ""
          } viewed portal reports`,
          value:
            engagementReport.value?.hasViewedPortalReports,
        },

        {
          label: `Has${
            engagementReport.value
              ?.hasExportedTransactions === false
              ? " not"
              : ""
          } exported transactions on portal`,
          value:
            engagementReport.value?.hasExportedTransactions,
        },
        {
          label: `Has${
            engagementReport.value?.hasImportedProducts ===
            false
              ? " not"
              : ""
          } imported products on portal (last 3 months)`,
          value:
            engagementReport.value?.hasImportedProducts,
        },
        {
          label: `Has${
            engagementReport.value
              ?.hasEditedPortalConfig === false
              ? " not"
              : ""
          } edited config on portal (last 3 months)`,
          value:
            engagementReport.value?.hasEditedPortalConfig,
        },
      ],
      color: getRagColor(
        engagementReport.value?.portalScore
      ),
    },
    features: {
      label: "Features",
      score: engagementReport.value?.featureScore,
      comparisons: {
        takepayments:
          engagementReport.value?.featureScoreTpAvg,
        mcc: engagementReport.value?.featureScoreMccAvg,
        sector:
          engagementReport.value?.featureScoreSectorAvg,
      },
      details: [
        {
          label: "Number of cashiers",
          value: engagementReport.value?.countOfCashiers,
        },
        {
          label: "Percentage of orders using cashiers",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingCashier,
        },
        {
          label: "Percentage of orders using tables",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingTable,
        },
        {
          label:
            "Percentage of orders using split payments",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingSplitPayment,
        },
        {
          label: "Percentage of orders using cash",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingCash,
        },
        {
          label: "Percentage of orders using gratuity",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingGratuity,
        },
        {
          label:
            "Percentage of orders using service charge",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingServiceCharge,
        },
        {
          label: "Percentage of orders that were open",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingOpenOrder,
        },
        {
          label: "Percentage of orders using collection",
          value:
            engagementReport.value
              ?.percentageOfOrdersUsingCollection,
        },
        {
          label: "Number of unlinked refunds",
          value:
            engagementReport.value?.countOfUnlinkedRefunds,
        },
      ],
      color: getRagColor(
        engagementReport.value?.featureScore
      ),
    },
  }))

const getBackgroundColorClass = (color: AvailableColor) =>
  `bg-${color}`
</script>
